import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: { sv: '', en: '' },
    description: { sv: '', en: '' },
    event_type: 'mass',
    church: 'karlskrona',
    start_time: '',
    end_time: '',
    location: '',
    recurring: false,
    recurring_pattern: '',
  });

  const loadEvents = async () => {
    const data = await api.getEvents();
    setEvents(data);
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        ...formData,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
      };
      
      if (editingEvent) {
        await api.updateEvent(editingEvent.id, eventData);
        toast.success('Kungörelse uppdaterad!');
      } else {
        await api.createEvent(eventData);
        toast.success('Kungörelse tillagd!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadEvents();
    } catch (error) {
      toast.error('Ett fel uppstod');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Är du säker?')) {
      await api.deleteEvent(id);
      toast.success('Kungörelse raderad!');
      loadEvents();
    }
  };

  const resetForm = () => {
    setFormData({
      title: { sv: '', en: '' },
      description: { sv: '', en: '' },
      event_type: 'mass',
      church: 'karlskrona',
      start_time: '',
      end_time: '',
      location: '',
      recurring: false,
      recurring_pattern: '',
    });
    setEditingEvent(null);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      event_type: event.event_type,
      church: event.church,
      start_time: new Date(event.start_time).toISOString().slice(0, 16),
      end_time: new Date(event.end_time).toISOString().slice(0, 16),
      location: event.location || '',
      recurring: event.recurring,
      recurring_pattern: event.recurring_pattern || '',
    });
    setIsDialogOpen(true);
  };

  const eventTypes = [
    { id: 'mass', name: 'Mässa' },
    { id: 'activity', name: 'Aktivitet' },
    { id: 'holiday', name: 'Högtid' },
  ];

  const churches = [
    { id: 'karlskrona', name: 'Karlskrona' },
    { id: 'karlshamn', name: 'Karlshamn' },
    { id: 'olofstrom', name: 'Olofström' },
  ];

  return (
    <div className="space-y-6" data-testid="events-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Kungörelser</h1>
          <p className="text-gray-600">Hantera mässor, aktiviteter och högtider</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-event-button">
              <Plus className="w-4 h-4 mr-2" />
              Lägg till Kungörelse
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Redigera Kungörelse' : 'Lägg till Kungörelse'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="event-form">
              <div>
                <label className="block text-sm font-medium mb-2">Titel (Svenska)</label>
                <Input
                  required
                  value={formData.title.sv}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, sv: e.target.value } })}
                  data-testid="event-title-sv"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Beskrivning (Svenska)</label>
                <Textarea
                  required
                  rows={3}
                  value={formData.description.sv}
                  onChange={(e) => setFormData({ ...formData, description: { ...formData.description, sv: e.target.value } })}
                  data-testid="event-description-sv"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Typ</label>
                  <select
                    value={formData.event_type}
                    onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                    data-testid="event-type"
                  >
                    {eventTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Kyrka</label>
                  <select
                    value={formData.church}
                    onChange={(e) => setFormData({ ...formData, church: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                    data-testid="event-church"
                  >
                    {churches.map((church) => (
                      <option key={church.id} value={church.id}>
                        {church.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Starttid</label>
                  <Input
                    required
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    data-testid="event-start-time"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Sluttid</label>
                  <Input
                    required
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    data-testid="event-end-time"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Plats (valfritt)</label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="T.ex. Vår Fru av Fatima kyrka"
                  data-testid="event-location"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.recurring}
                  onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                  className="rounded"
                  data-testid="event-recurring"
                />
                <label className="text-sm font-medium">Återkommande kungörelse</label>
              </div>
              {formData.recurring && (
                <div>
                  <label className="block text-sm font-medium mb-2">Återkommande mönster</label>
                  <select
                    value={formData.recurring_pattern}
                    onChange={(e) => setFormData({ ...formData, recurring_pattern: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                    data-testid="event-recurring-pattern"
                  >
                    <option value="">Välj...</option>
                    <option value="weekly">Veckovis</option>
                    <option value="monthly">Månadsvis</option>
                  </select>
                </div>
              )}
              <Button type="submit" className="w-full" data-testid="event-submit">
                {editingEvent ? 'Uppdatera' : 'Lägg till'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {events.map((event) => (
          <Card key={event.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      event.event_type === 'mass' ? 'bg-blue-100 text-blue-800' :
                      event.event_type === 'activity' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {eventTypes.find(t => t.id === event.event_type)?.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {churches.find(c => c.id === event.church)?.name}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title.sv}</h3>
                  <p className="text-gray-600 mb-2">{event.description.sv}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>📅 {new Date(event.start_time).toLocaleString('sv-SE')}</span>
                    {event.recurring && <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">Återkommande</span>}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(event)} data-testid={`edit-event-${event.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(event.id)} data-testid={`delete-event-${event.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default EventsManager;
